import type { ApiClient } from '@treneo/infrastructure/api';
import { authStatusChecker, BaseApi } from '@treneo/infrastructure/api';
import type {
  IChangePasswordRequest,
  IForgotPasswordRequest,
  ILogInAndJoinToCourseRequest,
  ILogInAndJoinToCourseResponse,
  ILogInOrSignUpViaGoogleRequest,
  ILogInOrSignUpViaLinkedInRequest,
  ILogInRequest,
  IRegisterAndJoinToCourseRequest,
  IRegisterAndJoinToCourseResponse,
  IRegisterRequest,
  IResetPasswordRequest,
  Result
} from '@treneo/models';
import { API_PATHS } from '@treneo/shared/constants';

export class AuthApi extends BaseApi {
  protected readonly baseUrl = API_PATHS.AUTH_PREFIX;

  constructor(apiClient: ApiClient) {
    super(apiClient);
  }

  login = async (request: ILogInRequest): Promise<void> => {
    const result = await this.post<void>('/login', request, 'Login failed');

    if (!result.isSuccess) {
      throw new Error(result.error ?? 'Login failed');
    }
  };

  loginAndJoinToCourse = (
    request: ILogInAndJoinToCourseRequest
  ): Promise<Result<ILogInAndJoinToCourseResponse | null>> =>
    this.post<ILogInAndJoinToCourseResponse | null>(
      '/login-and-join-to-course',
      request,
      'Login and join course failed'
    );

  loginOrSignUpViaGoogle = async (request: ILogInOrSignUpViaGoogleRequest): Promise<void> => {
    const result = await this.post<void>('/logInOrSignUpViaGoogle', request, 'Google login failed');

    if (!result.isSuccess) {
      throw new Error(result.error ?? 'Google login failed');
    }
  };

  loginOrSignUpViaLinkedIn = async (request: ILogInOrSignUpViaLinkedInRequest): Promise<void> => {
    const result = await this.post<void>(
      '/logInOrSignUpViaLinkedIn',
      request,
      'LinkedIn login failed'
    );

    if (!result.isSuccess) {
      throw new Error(result.error ?? 'LinkedIn login failed');
    }
  };

  register = (request: IRegisterRequest): Promise<void> => {
    const requestWithOrigin = {
      ...request,
      origin: globalThis.window.location.origin + import.meta.env.BASE_URL
    };
    return this.postVoid('/register', requestWithOrigin);
  };

  registerAndJoinToCourse = (
    request: IRegisterAndJoinToCourseRequest
  ): Promise<Result<IRegisterAndJoinToCourseResponse | null>> => {
    const requestWithOrigin = {
      ...request,
      origin: globalThis.window.location.origin + import.meta.env.BASE_URL
    };
    return this.post<IRegisterAndJoinToCourseResponse | null>(
      '/register-and-join-to-course',
      requestWithOrigin,
      'Register and join course failed'
    );
  };

  forgotPassword = (request: IForgotPasswordRequest): Promise<void> => {
    const requestWithOrigin = {
      ...request,
      origin: globalThis.window.location.origin + import.meta.env.BASE_URL
    };
    return this.postVoid('/forgotPassword', requestWithOrigin);
  };

  resetPassword = (request: IResetPasswordRequest): Promise<void> =>
    this.postVoid('/resetPassword', request);

  changePassword = (request: IChangePasswordRequest): Promise<void> =>
    this.postVoid('/changePassword', request);

  confirmEmailAndActivateAccount = (code: string): Promise<void> =>
    this.postVoid('/confirmEmailAndActivateAccount', { code });

  logout = (): Promise<void> => this.postVoid('/logout');

  isTokenExpiringSoon = (bufferMinutes = 5): Promise<boolean> =>
    authStatusChecker.isTokenExpiringSoon(bufferMinutes);

  refreshToken = async (): Promise<void> => {
    await this.postVoid('/refresh');
  };
}
